/**
 * Shared workload hour-distribution logic.
 *
 * Used by both the scheduler workload overlay (useWorkload.ts) and
 * the reports calculation (report-calculation.ts) to ensure a single
 * source of truth for how item hours are spread across calendar days.
 */

import { datesInRange } from './dates';

// ─── Constants ───────────────────────────────────────────────────────────────

const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

// ─── Working day counting ────────────────────────────────────────────────────

/**
 * Count effective working days in a date range, accounting for capacity
 * schedule and optional leaves.
 *
 * - Days with 0 capacity hours (per schedule) are skipped entirely.
 * - Full-day leave dates are skipped entirely.
 * - Half-day leave dates (am/pm) count as 0.5.
 * - All other working days count as 1.
 */
export function countWorkingDays(
  startDate: string,
  endDate: string,
  schedule: Readonly<Record<string, number>>,
  leaveLookup?: ReadonlyMap<string, 'full' | 'am' | 'pm'>,
): number {
  const dates = datesInRange(startDate, endDate);
  let count = 0;
  for (const dateStr of dates) {
    const date = new Date(dateStr + 'T00:00:00');
    const dayKey = DOW_KEYS[date.getDay()];
    if ((schedule[dayKey] ?? 0) <= 0) continue;
    const leaveType = leaveLookup?.get(dateStr);
    if (leaveType === 'full') continue;
    if (leaveType === 'am' || leaveType === 'pm') {
      count += 0.5;
    } else {
      count += 1;
    }
  }
  return count;
}

// ─── Hours-per-day computation ───────────────────────────────────────────────

export interface HoursPerDayInput {
  /** The hours value from the item */
  totalHours: number;
  /** How the hours value is interpreted */
  workHourFormat: 'per_day' | 'total';
  /** Full item timeline start (ISO yyyy-MM-dd) */
  timelineStart: string;
  /** Full item timeline end (ISO yyyy-MM-dd) */
  timelineEnd: string;
  /**
   * User's capacity schedule: day-of-week key ('mon','tue',...) → hours.
   * Only consulted when workHourFormat is 'total'.
   */
  schedule: Readonly<Record<string, number>>;
  /**
   * User's leave lookup: dateStr → 'full'|'am'|'pm'.
   * Only consulted when workHourFormat is 'total'.
   */
  leaveLookup?: ReadonlyMap<string, 'full' | 'am' | 'pm'>;
  /**
   * Number of assignees sharing this item's hours.
   * When > 1, hours are divided equally. Defaults to 1.
   */
  assigneeCount?: number;
}

/**
 * Compute the daily hours for a single user on a single item.
 *
 * Returns an unrounded number. Callers are responsible for rounding
 * to their desired precision.
 *
 * Does NOT decide which dates to emit hours on — that is the caller's
 * responsibility (e.g., the scheduler skips leave dates; reports does not).
 */
export function computeHoursPerDay(input: HoursPerDayInput): number {
  const {
    totalHours,
    workHourFormat,
    timelineStart,
    timelineEnd,
    schedule,
    leaveLookup,
    assigneeCount = 1,
  } = input;

  let hoursPerDay = totalHours;

  if (workHourFormat === 'total') {
    const workingDays = countWorkingDays(
      timelineStart,
      timelineEnd,
      schedule,
      leaveLookup,
    );
    hoursPerDay = workingDays > 0 ? totalHours / workingDays : 0;
  }

  if (assigneeCount > 1) {
    hoursPerDay = Math.round((hoursPerDay / assigneeCount) * 100) / 100;
  }

  return hoursPerDay;
}
